import { makeAutoObservable } from 'mobx'

import { userManager } from '@/Core/Managers'

import { DISK_SIMPLE_UPLOAD_MAX_MIB } from '../Contracts/DiskNode'
import {
    DISK_UPLOAD_CHUNK_RETRIES,
    DISK_UPLOAD_PARALLEL_CHUNKS,
    DISK_UPLOAD_PARALLEL_FILES,
} from '../Contracts/DiskUpload'
import { diskErrorText } from '../Helpers/diskErrors'
import { deleteUploadRecord, listUploadRecords, saveUploadRecord } from '../Helpers/diskUploadPersistence'
import {
    chunkRange,
    missingChunks,
    nameKey,
    pushSample,
    receivedBytes,
    resumeMismatch,
    transferRate,
} from '../Helpers/diskUploadPlan'
import { fileSha256 } from '../Helpers/diskUploadSources'
import { CancelDiskUploadTask } from '../Tasks/CancelDiskUploadTask'
import { CompleteDiskUploadTask } from '../Tasks/CompleteDiskUploadTask'
import { GetDiskUploadTask } from '../Tasks/GetDiskUploadTask'
import { LoadDiskFolderItemsTask } from '../Tasks/LoadDiskFolderItemsTask'
import { PutDiskUploadChunkTask } from '../Tasks/PutDiskUploadChunkTask'
import { StartDiskUploadTask } from '../Tasks/StartDiskUploadTask'
import { UploadDiskFileTask } from '../Tasks/UploadDiskFileTask'

import type {
    DiskConflictChoice,
    DiskPickedFile,
    DiskUploadConflict,
    DiskUploadRecord,
    DiskUploadSession,
    DiskUploadSource,
    DiskUploadState,
    DiskUploadTarget,
} from '../Contracts/DiskUpload'
import type { RateSample } from '../Helpers/diskUploadPlan'

const SIMPLE_MAX_BYTES = DISK_SIMPLE_UPLOAD_MAX_MIB * 1024 * 1024
/** Пауза між повторами шматка: 1 с, 2 с, 4 с. */
const RETRY_BASE_MS = 1000
/** Оновлення папки й квоти після серії файлів — одним запитом, а не на кожен файл. */
const REFRESH_DELAY_MS = 600

export interface DiskUploadItem {
    id: string
    /** Файли одного вибору/перетягування — для «Застосувати до всіх». */
    batchId: string
    /** `null` — сесію відновлено після перезавантаження, файл треба обрати знову. */
    file: File | null
    name: string
    size: number
    lastModified: number
    relativePath: string
    target: DiskUploadTarget
    conflict: DiskUploadConflict
    state: DiskUploadState
    uploadedBytes: number
    /** Байт/с за останні секунди; 0 — невідомо. */
    speed: number
    sessionUuid: string | null
    error: string | null
}

/** Що сталося з файлами, які щойно завершилися: оновити ці папки й квоту. */
export interface DiskUploadedBatch {
    parents: string[]
    /** Чи з'явилися нові папки (завантаження папки) — тоді й дерево. */
    withFolders: boolean
}

type UploadedListener = (batch: DiskUploadedBatch) => void

const ACTIVE: readonly DiskUploadState[] = ['queued', 'uploading']
const FINISHED: readonly DiskUploadState[] = ['done', 'cancelled', 'skipped']

let counter = 0
const localId = (prefix: string): string => `${prefix}-${Date.now().toString(36)}-${(counter += 1)}`

const httpStatus = (error: unknown): number | null => {
    const raw = error as { statusCode?: unknown; response?: { status?: unknown } } | null
    const status = raw?.statusCode ?? raw?.response?.status

    return typeof status === 'number' ? status : null
}

/** Конфлікт назви з `conflict=fail`: 422 із порушенням на полі `name`. */
const isNameConflict = (error: unknown): boolean => {
    if (httpStatus(error) !== 422) return false

    const violations = (error as { response?: { violations?: unknown } } | null)?.response?.violations

    return (
        Array.isArray(violations) &&
        violations.some((violation) => (violation as { field?: unknown } | null)?.field === 'name')
    )
}

/** Обрив зв'язку, тайм-аут, 5xx, 429 — варто повторити той самий шматок. */
const isTransient = (error: unknown): boolean => {
    const status = httpStatus(error)

    return status === null || status >= 500 || status === 429 || status === 408
}

const wait = (ms: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve) => {
        const timer = setTimeout(resolve, ms)

        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timer)
                resolve()
            },
            { once: true }
        )
    })

const ownerKey = (): string => String(userManager.user['uuid'] ?? userManager.user.id ?? '')

/**
 * Черга завантажень диска (`DISK-001` §6, наряд Д1.10). Глобальна: живе поза
 * екраном диска, тож перехід в інший модуль завантаження не перериває, а
 * панель менеджера стоїть у каркасі застосунку.
 *
 * - Одночасно — `DISK_UPLOAD_PARALLEL_FILES` файлів, у кожного —
 *   `DISK_UPLOAD_PARALLEL_CHUNKS` шматків. Файл до 8 МіБ іде одним запитом,
 *   більший — шматками з докачуванням.
 * - Сесію шматків записуємо в IndexedDB: після перезавантаження сторінки
 *   вона чекає, поки користувач обере той самий файл, і докачує відсутнє.
 * - Окремі файли з зайнятою назвою чекають на відповідь у діалозі; файли
 *   папки зберігаються поруч («Назва (1)»).
 */
export class UploadQueueStore {
    items: DiskUploadItem[] = []
    collapsed = false

    private controllers = new Map<string, AbortController>()
    private samples = new Map<string, RateSample[]>()
    /** «Застосувати до всіх» — відповідь на конфлікт для решти файлів того самого вибору. */
    private batchChoices = new Map<string, DiskConflictChoice>()
    private listeners = new Set<UploadedListener>()
    private refreshTimer: ReturnType<typeof setTimeout> | null = null
    private pendingParents = new Set<string>()
    private pendingFolders = false
    private restored = false

    constructor() {
        makeAutoObservable<
            UploadQueueStore,
            | 'controllers'
            | 'samples'
            | 'batchChoices'
            | 'listeners'
            | 'refreshTimer'
            | 'pendingParents'
            | 'pendingFolders'
            | 'restored'
        >(this, {
            controllers: false,
            samples: false,
            batchChoices: false,
            listeners: false,
            refreshTimer: false,
            pendingParents: false,
            pendingFolders: false,
            restored: false,
        })
    }

    // ---- Що на панелі ------------------------------------------------------

    get isVisible(): boolean {
        return this.items.length > 0
    }

    /** Щось вантажиться або чекає своєї черги — попереджаємо перед виходом зі сторінки. */
    get isBusy(): boolean {
        return this.items.some((item) => ACTIVE.includes(item.state))
    }

    get hasFinished(): boolean {
        return this.items.some((item) => FINISHED.includes(item.state))
    }

    /** Перший файл, що чекає на відповідь про зайняту назву. */
    get pendingConflict(): DiskUploadItem | null {
        return this.items.find((item) => item.state === 'conflict') ?? null
    }

    /** Скільки ще конфліктів у тому самому виборі, крім показаного. */
    conflictsInBatch(item: DiskUploadItem): number {
        return this.items.filter(
            (other) => other.id !== item.id && other.batchId === item.batchId && other.state === 'conflict'
        ).length
    }

    /** Загальний поступ того, що ще не скасовано й не пропущено. */
    get progress(): { done: number; total: number; bytes: number; totalBytes: number; percent: number } {
        const counted = this.items.filter((item) => !['cancelled', 'skipped'].includes(item.state))
        const bytes = counted.reduce((sum, item) => sum + item.uploadedBytes, 0)
        const totalBytes = counted.reduce((sum, item) => sum + item.size, 0)

        return {
            done: counted.filter((item) => item.state === 'done').length,
            total: counted.length,
            bytes,
            totalBytes,
            percent: totalBytes === 0 ? (counted.length > 0 ? 100 : 0) : Math.floor((bytes * 100) / totalBytes),
        }
    }

    toggleCollapsed(): void {
        this.collapsed = !this.collapsed
    }

    /** Підписка екрана диска: оновити відкриту папку й картку квоти. */
    onUploaded(listener: UploadedListener): () => void {
        this.listeners.add(listener)

        return () => this.listeners.delete(listener)
    }

    // ---- Додати в чергу ----------------------------------------------------

    /**
     * Поставити файли в чергу. Окремі файли (`source: 'files'`) з назвою, що
     * вже є в папці, одразу чекають на рішення в діалозі; решта йде з
     * `conflict=fail`, і зайнятість, яку не видно в списку, бек поверне 422 —
     * тоді той самий діалог. Файли папки — `keep_both`.
     */
    async enqueue(picked: DiskPickedFile[], target: DiskUploadTarget, source: DiskUploadSource): Promise<void> {
        if (picked.length === 0) return

        // Той самий файл, що чекає після перезавантаження, — продовжуємо його, а не вантажимо вдруге
        const fresh = picked.filter((entry) => !this.attachMatching(entry))
        if (fresh.length === 0) return

        const ask = source === 'files'
        const taken = ask && fresh.some((entry) => entry.relativePath === '') ? await this.takenNames(target.parent) : null
        this.addItems(fresh, target, ask, taken)
    }

    private addItems(
        fresh: DiskPickedFile[],
        target: DiskUploadTarget,
        ask: boolean,
        taken: Set<string> | null
    ): void {
        const batchId = localId('batch')

        this.collapsed = false

        for (const { file, relativePath } of fresh) {
            const conflict: DiskUploadConflict = ask && relativePath === '' ? 'fail' : 'keep_both'
            const clash = conflict === 'fail' && taken !== null && taken.has(nameKey(file.name))

            this.items.push({
                id: localId('upload'),
                batchId,
                file,
                name: file.name,
                size: file.size,
                lastModified: file.lastModified,
                relativePath,
                target,
                conflict,
                state: clash ? 'conflict' : 'queued',
                uploadedBytes: 0,
                speed: 0,
                sessionUuid: null,
                error: null,
            })
        }

        this.pump()
    }

    /** Назви, зайняті в папці (до стелі сторінки списку); не вдалося — питатимемо на 422. */
    private async takenNames(parent: string): Promise<Set<string> | null> {
        try {
            const { items } = await LoadDiskFolderItemsTask({ parent })

            return new Set(items.map((node) => nameKey(node.name)))
        } catch {
            return null
        }
    }

    private attachMatching({ file, relativePath }: DiskPickedFile): boolean {
        const waiting = this.items.find(
            (item) =>
                item.state === 'needsFile' &&
                item.relativePath === relativePath &&
                resumeMismatch(item, file) === null
        )

        if (!waiting) return false

        this.patch(waiting.id, { file, state: 'queued', error: null })

        return true
    }

    // ---- Керування -------------------------------------------------------

    pause(id: string): void {
        const item = this.find(id)
        if (!item || !ACTIVE.includes(item.state)) return

        this.patch(id, { state: 'paused', speed: 0 })
        this.controllers.get(id)?.abort()
        this.pump()
    }

    resume(id: string): void {
        const item = this.find(id)
        if (!item || item.state !== 'paused') return

        this.patch(id, { state: 'queued', error: null })
        this.pump()
    }

    retry(id: string): void {
        const item = this.find(id)
        if (!item || item.state !== 'error' || item.file === null) return

        this.patch(id, { state: 'queued', error: null })
        this.pump()
    }

    /** Скасувати: перервати запити, звільнити резерв квоти на беку, забути сесію. */
    cancel(id: string): void {
        const item = this.find(id)
        if (!item || FINISHED.includes(item.state)) return

        const session = item.sessionUuid

        this.patch(id, { state: 'cancelled', speed: 0, sessionUuid: null })
        this.controllers.get(id)?.abort()
        this.pump()

        if (session !== null) void this.dropSession(session)
    }

    /** Прибрати з панелі готові, скасовані й пропущені. */
    clearFinished(): void {
        this.items = this.items.filter((item) => !FINISHED.includes(item.state))
        if (this.items.length === 0) this.collapsed = false
    }

    /** Закрити панель, коли все завершено: сесії файлів із помилкою більше не потрібні. */
    dismiss(): void {
        if (this.isBusy) return

        for (const item of this.items) {
            if (item.state === 'error' && item.sessionUuid !== null) void this.dropSession(item.sessionUuid)
        }

        this.items = this.items.filter((item) => ![...FINISHED, 'error'].includes(item.state))
        this.collapsed = false
    }

    /**
     * Відповідь у діалозі конфлікту. «Застосувати до всіх» — для решти
     * конфліктів цього вибору, зокрема тих, що бек поверне пізніше.
     */
    resolveConflict(id: string, choice: DiskConflictChoice, applyToAll: boolean): void {
        const item = this.find(id)
        if (!item) return

        const targets = applyToAll
            ? this.items.filter((other) => other.batchId === item.batchId && other.state === 'conflict')
            : [item]

        if (applyToAll) this.batchChoices.set(item.batchId, choice)

        for (const target of targets) this.applyChoice(target.id, choice)

        this.pump()
    }

    private applyChoice(id: string, choice: DiskConflictChoice): void {
        const item = this.find(id)
        if (!item) return

        // Режим конфлікту записано в сесії — з іншою відповіддю потрібна нова
        const session = item.sessionUuid
        if (session !== null) void this.dropSession(session)

        if (choice === 'skip') {
            this.patch(id, { state: 'skipped', sessionUuid: null, uploadedBytes: 0 })

            return
        }

        this.patch(id, { conflict: choice, state: 'queued', sessionUuid: null, uploadedBytes: 0, error: null })
    }

    /**
     * Файл, обраний для продовження сесії після перезавантаження. Повертає
     * пояснення, якщо це не той файл.
     */
    attachFile(id: string, file: File): string | null {
        const item = this.find(id)
        if (!item || item.state !== 'needsFile') return null

        const mismatch = resumeMismatch(item, file)
        if (mismatch !== null) return mismatch

        this.patch(id, { file, state: 'queued', error: null })
        this.pump()

        return null
    }

    // ---- Відновлення після перезавантаження -------------------------------

    /**
     * Незавершені сесії з IndexedDB: живі на беку чекають на файл, решту
     * (прострочені, скасовані, чужі) забуваємо.
     */
    async restore(): Promise<void> {
        if (this.restored) return

        const owner = ownerKey()
        if (owner === '') return

        this.restored = true

        for (const record of await listUploadRecords(owner)) {
            if (this.items.some((item) => item.sessionUuid === record.uploadUuid)) continue

            let session: DiskUploadSession | null = null

            try {
                session = await GetDiskUploadTask(record.uploadUuid)
            } catch (error) {
                // 404 — сесії вже немає; без зв'язку — спробуємо наступного разу
                if (httpStatus(error) !== null) await deleteUploadRecord(record.uploadUuid)
                continue
            }

            if (session.status !== 'active') {
                await deleteUploadRecord(record.uploadUuid)
                continue
            }

            this.addRestored(record, session)
        }
    }

    private addRestored(record: DiskUploadRecord, session: DiskUploadSession): void {
        this.items.push({
            id: localId('upload'),
            batchId: localId('batch'),
            file: null,
            name: record.name,
            size: record.size,
            lastModified: record.lastModified,
            relativePath: record.relativePath,
            target: { parent: record.parent, parentName: record.parentName, storageUuid: record.storageUuid },
            conflict: record.conflict,
            state: 'needsFile',
            uploadedBytes: receivedBytes(session.size, session.chunkSize, session.received),
            speed: 0,
            sessionUuid: record.uploadUuid,
            error: null,
        })
    }

    // ---- Виконання ---------------------------------------------------------

    /** Запустити наступні файли черги, поки є вільні місця. */
    private pump(): void {
        let running = this.items.filter((item) => item.state === 'uploading').length

        for (const item of this.items) {
            if (running >= DISK_UPLOAD_PARALLEL_FILES) break
            if (item.state !== 'queued' || item.file === null) continue

            running += 1
            this.patch(item.id, { state: 'uploading', error: null })
            void this.run(item.id)
        }
    }

    private async run(id: string): Promise<void> {
        const item = this.find(id)
        const file = item?.file
        if (!item || !file) return

        const controller = new AbortController()
        this.controllers.set(id, controller)
        this.samples.set(id, [])

        try {
            const simple = file.size <= SIMPLE_MAX_BYTES && item.sessionUuid === null

            if (simple) {
                const sha256 = await fileSha256(file)
                if (controller.signal.aborted) return

                await UploadDiskFileTask({
                    parent: item.target.parent,
                    file,
                    conflict: item.conflict,
                    relativePath: item.relativePath,
                    sha256,
                    signal: controller.signal,
                })
            } else {
                await this.runChunked(id, file, controller.signal)
            }

            if (controller.signal.aborted) return

            this.patch(id, { state: 'done', uploadedBytes: file.size, speed: 0, sessionUuid: null })
            this.scheduleRefresh(item.target.parent, item.relativePath !== '')
        } catch (error) {
            if (controller.signal.aborted) return
            this.fail(id, error)
        } finally {
            if (this.controllers.get(id) === controller) this.controllers.delete(id)
            this.samples.delete(id)
            this.pump()
        }
    }

    private fail(id: string, error: unknown): void {
        const item = this.find(id)
        if (!item) return

        if (item.conflict === 'fail' && isNameConflict(error)) {
            const choice = this.batchChoices.get(item.batchId)

            this.patch(id, { state: 'conflict', speed: 0, uploadedBytes: 0 })
            if (choice !== undefined) this.applyChoice(id, choice)

            return
        }

        this.patch(id, { state: 'error', speed: 0, error: diskErrorText(error, 'Не вдалося завантажити файл') })
    }

    /** Шматки: нова сесія або докачування наявної — лише відсутні номери. */
    private async runChunked(id: string, file: File, signal: AbortSignal): Promise<void> {
        const session = await this.openSession(id, file, signal)
        if (signal.aborted) return

        const missing = missingChunks(session.size, session.chunkSize, session.received)
        let done = receivedBytes(session.size, session.chunkSize, session.received)

        this.reportProgress(id, done)

        let failure: unknown = null
        const queue = [...missing]

        const worker = async (): Promise<void> => {
            while (queue.length > 0 && failure === null && !signal.aborted) {
                const number = queue.shift()
                if (number === undefined) return

                const range = chunkRange(number, session.size, session.chunkSize)
                if (range === null) continue

                try {
                    await this.putChunk(session.uuid, number, file.slice(range.start, range.end), signal)
                    done += range.end - range.start
                    this.reportProgress(id, done)
                } catch (error) {
                    if (!signal.aborted && failure === null) failure = error
                }
            }
        }

        await Promise.all(Array.from({ length: Math.min(DISK_UPLOAD_PARALLEL_CHUNKS, missing.length) }, worker))

        if (signal.aborted) return
        if (failure !== null) {
            // Сесії більше немає (прострочена чи скасована деінде) — «Повторити» почне нову
            if (httpStatus(failure) === 404) await this.forgetSession(id)
            throw failure
        }

        try {
            await CompleteDiskUploadTask(session.uuid)
        } catch (error) {
            if (httpStatus(error) === 404) await this.forgetSession(id)
            throw error
        }

        await deleteUploadRecord(session.uuid)
    }

    private async openSession(id: string, file: File, signal: AbortSignal): Promise<DiskUploadSession> {
        const item = this.find(id)
        if (!item) throw new Error('Файл зник із черги')

        if (item.sessionUuid !== null) {
            try {
                const session = await GetDiskUploadTask(item.sessionUuid)

                if (session.status === 'active') return session
            } catch (error) {
                if (httpStatus(error) !== 404) throw error
            }

            await this.forgetSession(id)
        }

        const sha256 = await fileSha256(file)
        const current = this.find(id)
        if (!current || signal.aborted) throw new Error('Перервано')

        const session = await StartDiskUploadTask({
            parent: current.target.parent,
            name: current.name,
            size: file.size,
            conflict: current.conflict,
            relativePath: current.relativePath,
            sha256,
            // Без `signal`: перерваний старт лишив би на беку сесію з резервом квоти, про яку ми не знаємо
        })

        // Поки сесія відкривалася, файл скасували — сесія не потрібна (на паузі — лишається)
        const state = this.find(id)?.state
        if (state === undefined || state === 'cancelled' || state === 'skipped') {
            void this.dropSession(session.uuid)
            throw new Error('Перервано')
        }

        this.patch(id, { sessionUuid: session.uuid })

        const owner = ownerKey()
        if (owner !== '') {
            await saveUploadRecord({
                uploadUuid: session.uuid,
                ownerKey: owner,
                parent: current.target.parent,
                parentName: current.target.parentName,
                storageUuid: current.target.storageUuid,
                name: current.name,
                size: file.size,
                lastModified: file.lastModified,
                relativePath: current.relativePath,
                conflict: current.conflict,
                createdAt: Date.now(),
            })
        }

        // Пауза під час старту: сесія вже є, її докачає «Продовжити»
        if (signal.aborted) throw new Error('Перервано')

        return session
    }

    /** Шматок із повторами при обриві зв'язку; 4xx не повторюємо. */
    private async putChunk(uuid: string, number: number, chunk: Blob, signal: AbortSignal): Promise<void> {
        for (let attempt = 0; ; attempt += 1) {
            try {
                await PutDiskUploadChunkTask(uuid, number, chunk, signal)

                return
            } catch (error) {
                if (signal.aborted || attempt >= DISK_UPLOAD_CHUNK_RETRIES || !isTransient(error)) throw error

                await wait(RETRY_BASE_MS * 2 ** attempt, signal)
            }
        }
    }

    private reportProgress(id: string, bytes: number): void {
        const samples = pushSample(this.samples.get(id) ?? [], { at: Date.now(), bytes })

        this.samples.set(id, samples)
        this.patch(id, { uploadedBytes: bytes, speed: transferRate(samples) })
    }

    private async forgetSession(id: string): Promise<void> {
        const session = this.find(id)?.sessionUuid ?? null

        this.patch(id, { sessionUuid: null })
        if (session !== null) await deleteUploadRecord(session)
    }

    private async dropSession(uuid: string): Promise<void> {
        await deleteUploadRecord(uuid)

        try {
            await CancelDiskUploadTask(uuid)
        } catch {
            // Сесії вже немає — резерв звільнено; решту прибере планова команда беку
        }
    }

    /** Оновлення відкритої папки й квоти — одне на серію файлів. */
    private scheduleRefresh(parent: string, withFolders: boolean): void {
        this.pendingParents.add(parent)
        this.pendingFolders = this.pendingFolders || withFolders

        if (this.refreshTimer !== null) clearTimeout(this.refreshTimer)

        this.refreshTimer = setTimeout(() => {
            const batch: DiskUploadedBatch = { parents: [...this.pendingParents], withFolders: this.pendingFolders }

            this.pendingParents.clear()
            this.pendingFolders = false
            this.refreshTimer = null

            for (const listener of this.listeners) listener(batch)
        }, REFRESH_DELAY_MS)
    }

    private find(id: string): DiskUploadItem | undefined {
        return this.items.find((item) => item.id === id)
    }

    private patch(id: string, patch: Partial<DiskUploadItem>): void {
        const item = this.find(id)

        if (item) Object.assign(item, patch)
    }
}

export const uploadQueueStore = new UploadQueueStore()
